import { Flex, Text, FlexProps, TextProps } from '@chakra-ui/react';
import { IconButton, IconButtonProps } from '@gamma/ui/buttons';
import ChevronLeftSharpIcon from '@gamma/ui/icons/ChevronLeftSharpIcon';
import ChevronRightSharpIcon from '@gamma/ui/icons/ChevronRightSharpIcon';
import { Dispatch, SetStateAction, useEffect } from 'react';

export interface SimpleCaretPaginationProps {
  currentPage: number;
  pageLength: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  /** Optional props to style the container Flex */
  containerProps?: Omit<FlexProps, 'children'>;
  /** Optional props to style the text display */
  textProps?: Omit<TextProps, 'children'>;
  /** Optional props to style the icon buttons */
  buttonProps?: Partial<
    Omit<IconButtonProps, 'onClick' | 'isDisabled' | 'aria-label' | 'icon'>
  >;
}

export const SimpleCaretPagination = ({
  currentPage,
  pageLength,
  setCurrentPage,
  containerProps,
  textProps,
  buttonProps,
}: SimpleCaretPaginationProps) => {
  // Boundary check: reset to page 0 if current page is out of bounds
  useEffect(() => {
    if (pageLength > 0 && currentPage >= pageLength) {
      setCurrentPage(0);
    }
  }, [pageLength, currentPage, setCurrentPage]);

  const handleNext = () => {
    if (currentPage < pageLength - 1) {
      setCurrentPage(currentPage + 1);
    }
  };
  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    pageLength > 1 && (
      <Flex justify="space-between" align="center" gap={2} {...containerProps}>
        <IconButton
          icon={<ChevronLeftSharpIcon />}
          variant="ghost"
          color="text.primary"
          onClick={handlePrevious}
          isDisabled={currentPage === 0}
          aria-label="Previous"
          _disabled={{
            color: 'text.secondary',
            cursor: 'not-allowed',
            opacity: 0.4,
          }}
          {...buttonProps}
        />
        <Text fontSize="md" {...textProps}>
          {currentPage + 1}/{pageLength}
        </Text>
        <IconButton
          icon={<ChevronRightSharpIcon />}
          variant="ghost"
          color="text.primary"
          onClick={handleNext}
          isDisabled={currentPage === pageLength - 1}
          aria-label="Next"
          _disabled={{
            color: 'text.secondary',
            cursor: 'not-allowed',
            opacity: 0.4,
          }}
          {...buttonProps}
        />
      </Flex>
    )
  );
};
